import { ReactNode, useMemo, useState } from "react";
import { Braces, ChevronRight, Copy } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/shadcn/collapsible";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/utils/generic";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { TYPE_META } from "@/icons/DataTypeIcon";
import {
  ioMarkdownText,
  TypedValueBadge,
} from "@/components/SpotflowV2/Sidebar/TypedValueBadge";
import {
  NoxusJsonView,
  tryParseJsonObject,
} from "@/components/UI/NoxusJsonView";
import {
  coerceIoFileLike,
  coerceIoTextContainer,
} from "@/components/Spotflow/WorkflowRunner/Outputs/ioFileDisplay";
import { isSkippedValue } from "@/components/Spotflow/WorkflowRunner/Outputs/skippedDisplay";
import { ExpandableContent } from "@/components/ExpandableContent";

export const IO_VALUE_COLLAPSE_HEIGHT = 600;

export function ioNativeObject(value: unknown): object | null {
  if (
    value === null ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    isSkippedValue(value) ||
    coerceIoTextContainer(value) ||
    coerceIoFileLike(value)
  ) {
    return null;
  }
  return value;
}

export function ioParsedJson(value: unknown): object | unknown[] | null {
  const textContainer = coerceIoTextContainer(value);
  // A truncated preview is cut-off JSON.
  if (textContainer?.has_preview) return null;
  return (
    tryParseJsonObject(textContainer?.text ?? value) ?? ioNativeObject(value)
  );
}

function ioListHasParsedJsonItems(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.some((item) => tryParseJsonObject(item) !== null)
  );
}

export function IoValueBody({
  value,
  dataType,
  isList = false,
  showRaw,
}: {
  value: unknown;
  dataType: string;
  isList?: boolean;
  showRaw: boolean;
}) {
  const parsedJson = useMemo(() => ioParsedJson(value), [value]);
  const nativeObject = useMemo(() => ioNativeObject(value), [value]);
  return (
    <ExpandableContent
      defaultCollapsed
      min_height={IO_VALUE_COLLAPSE_HEIGHT}
      fadeToClassName="to-white dark:to-card"
    >
      {parsedJson && !showRaw ? (
        <NoxusJsonView data={parsedJson} expand={3} />
      ) : nativeObject ? (
        <pre className="text-sm font-mono whitespace-pre-wrap break-words">
          {JSON.stringify(nativeObject, null, 2)}
        </pre>
      ) : (
        <TypedValueBadge
          value={value}
          dataType={dataType}
          isList={isList}
          showTypeChip={false}
          wrapContent
          showAllListItems
          markdown={!showRaw}
          rawListItems={showRaw}
        />
      )}
    </ExpandableContent>
  );
}

export function IoValueCard({
  header,
  value,
  dataType,
  isList = false,
  chipIsList,
  isError = false,
  defaultOpen = false,
  contentClassName,
  footer,
  children,
}: {
  header: ReactNode;
  value: unknown;
  dataType: string;
  isList?: boolean;
  // Chip list-ness when it differs from the body's — a paged binding shows
  // one item at a time but is still a list.
  chipIsList?: boolean;
  isError?: boolean;
  defaultOpen?: boolean;
  contentClassName?: string;
  footer?: ReactNode;
  children: (showRaw: boolean) => ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [showRaw, setShowRaw] = useState(false);
  const meta = TYPE_META[dataType] ?? TYPE_META["str"];
  const hasRawView = useMemo(
    () =>
      !isError &&
      (ioParsedJson(value) !== null ||
        (isList
          ? ioListHasParsedJsonItems(value)
          : ioMarkdownText(value, dataType) !== null)),
    [isError, value, isList, dataType]
  );
  const copyText = useMemo(() => {
    if (typeof value === "string") return value;
    if (value == null) return "";
    // Stringifying a list of JSON strings escapes every inner quote and
    // newline — embed parseable items as objects so the copy is clean JSON.
    if (Array.isArray(value)) {
      return JSON.stringify(
        value.map((item) => tryParseJsonObject(item) ?? item),
        null,
        2
      );
    }
    return JSON.stringify(value, null, 2);
  }, [value]);

  return (
    <Collapsible
      data-testid="io-value-card"
      open={open}
      onOpenChange={setOpen}
      className="w-full min-w-0 max-w-full rounded-lg border border-border/60 bg-white dark:bg-card overflow-hidden"
    >
      <CollapsibleTrigger
        data-testid="io-value-row"
        className={cn(
          "group/row relative flex w-full min-w-0 items-center gap-2 px-2 py-1.5 text-left transition-colors cursor-pointer",
          isError
            ? "bg-red-50 hover:bg-red-100 dark:bg-red-950/40 dark:hover:bg-red-950/60"
            : "bg-gray-50 hover:bg-gray-100 dark:bg-muted/40 dark:hover:bg-muted/60"
        )}
      >
        <ChevronRight
          size={14}
          strokeWidth={2}
          className="shrink-0 text-muted-foreground/70 transition-transform duration-150 group-data-[state=open]/row:rotate-90"
        />
        {header}
        {/* Right slot: badge fades out, action buttons fade in — same absolute position, no layout shift */}
        <div className="absolute right-2 flex items-center">
          <span className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-1.5 py-0.5 text-xs font-medium text-muted-foreground transition-opacity group-hover/row:opacity-0 pointer-events-none">
            <meta.icon size={12} strokeWidth={1.75} />
            {(chipIsList ?? isList) ? `List of ${meta.label}` : meta.label}
          </span>
          <div
            className="absolute right-0 flex items-center gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}
          >
            {hasRawView && (
              <NoxusTooltip content={showRaw ? "Hide raw" : "Show raw"} asChild>
                <Button
                  data-testid="io-value-raw"
                  variant="ghost"
                  size="iconsm"
                  onClick={() => setShowRaw(!showRaw)}
                  className={showRaw ? "bg-muted" : "bg-transparent"}
                >
                  <Braces
                    size={13}
                    strokeWidth={1.33}
                    className="text-muted-foreground"
                  />
                </Button>
              </NoxusTooltip>
            )}
            <NoxusTooltip content="Copy" asChild>
              <Button
                data-testid="io-value-copy"
                variant="ghost"
                size="iconsm"
                onClick={() => copyToClipboard(copyText)}
              >
                <Copy
                  size={13}
                  strokeWidth={1.33}
                  className="text-muted-foreground"
                />
              </Button>
            </NoxusTooltip>
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div
          className={cn(
            "border-t border-border/60 px-3 py-2.5 min-w-0 w-full max-w-full text-sm leading-relaxed",
            contentClassName
          )}
        >
          {children(showRaw)}
        </div>
        {footer}
      </CollapsibleContent>
    </Collapsible>
  );
}
